import { useEffect, useState, type JSX } from 'react';
import { Link } from 'react-router-dom';
import { api, type Leaderboard as Board, type Standing } from '../api.ts';

/**
 * The standings.
 *
 * The scoring is printed above the table on purpose. A ranking whose arithmetic is hidden reads as
 * an opinion about people; one that shows its working reads as a measurement, and can be argued
 * with — which is the only way anybody comes to trust it.
 *
 * Your own row is marked and your place is stated at the top, so nobody has to scroll a table on a
 * phone to find out where they stand.
 */

/** What each part is called and what it is worth, in the order the table shows them. */
const PARTS: { key: keyof Standing['parts']; label: string; max: number; hint: string }[] = [
  {
    key: 'completion',
    label: 'Выполнено',
    max: 75,
    hint: 'доля всего, что просят уроки: видео, презентация, домашняя работа, тест',
  },
  {
    key: 'accuracy',
    label: 'Правильность',
    max: 25,
    hint: 'лучший результат каждого теста из всех попыток',
  },
];

/** A share as a percentage, or a dash when there is nothing to show yet. */
function percent(share: number | null): string {
  return share === null ? '—' : `${Math.round(share * 100)}%`;
}

export function LeaderboardPage(): JSX.Element {
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;

    api
      .leaderboard()
      .then((data) => alive && setBoard(data))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'Не удалось загрузить рейтинг'),
      );

    return () => {
      alive = false;
    };
  }, []);

  if (error) {
    return (
      <div className="stack" style={{ paddingTop: 40 }}>
        <div className="notice notice--accent">{error}</div>
        <Link to="/" className="back">
          ← Мои курсы
        </Link>
      </div>
    );
  }

  if (!board) {
    return (
      <p className="muted" style={{ paddingTop: 40 }}>
        Загружаем…
      </p>
    );
  }

  return (
    <>
      <div className="page-head">
        <h1>Рейтинг</h1>
        <p className="muted">
          Высший балл — за выполнение всего, что просят уроки. Скорость не считается: посмотреть
          видео, прочитать презентацию и сделать домашнюю работу требует времени, и платить за
          спешку значило бы платить за пропуски.
        </p>
      </div>

      {board.you ? (
        <div className="notice notice--accent">
          Вы на <strong>{board.you.place}</strong> месте из {board.total} · {board.you.score} баллов
        </div>
      ) : (
        <div className="notice">Вас нет в таблице — она составляется среди учеников школы.</div>
      )}

      <div className="scoring">
        {PARTS.map((part) => (
          <div key={part.key} className="scoring__item">
            <span className="scoring__name">
              {part.label} <span className="scoring__max">до {part.max}</span>
            </span>
            <span className="scoring__hint">{part.hint}</span>
          </div>
        ))}
      </div>

      {board.standings.length === 0 ? (
        <div className="notice">Пока ранжировать некого.</div>
      ) : (
        /*
         * Wide on purpose, and allowed to scroll sideways rather than dropping columns: the parts
         * are the point of the table, and a score with no explanation is the thing this page
         * exists to avoid.
         */
        <div className="board">
          <table className="board__table">
            <thead>
              <tr>
                <th>#</th>
                <th>Ученик</th>
                <th>Баллы</th>
                {PARTS.map((part) => (
                  <th key={part.key}>{part.label}</th>
                ))}
                <th>Уроков</th>
                <th>Сделано</th>
                <th>Верных</th>
                <th>Тестов</th>
              </tr>
            </thead>
            <tbody>
              {board.standings.map((row) => (
                <tr
                  key={row.profile_id}
                  className={board.you && row.place === board.you.place ? 'board__mine' : undefined}
                >
                  <td className="board__place">{row.place}</td>
                  <td>{row.name}</td>
                  <td className="board__score">{row.score}</td>
                  {PARTS.map((part) => (
                    <td key={part.key} className="board__part">
                      {row.parts[part.key]}
                    </td>
                  ))}
                  <td className="board__part">
                    {row.lessons_done}/{row.lessons_total}
                  </td>
                  <td className="board__part">{Math.round(row.completion * 100)}%</td>
                  <td className="board__part">{percent(row.accuracy)}</td>
                  <td className="board__part">
                    {row.quizzes_passed}/{row.quizzes_total}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="muted" style={{ marginTop: 18, fontSize: '0.85rem' }}>
        «Сделано» — доля выполненных условий по всем урокам курса, включая начатые. Правильность —
        лучший результат по каждому тесту из всех попыток: пересдали лучше — место в рейтинге тоже
        поднимется.
      </p>
    </>
  );
}
